import { useState, useEffect } from 'react';
import Swal from 'sweetalert2';
import api from '../services/api';
import EmptyState from '../components/EmptyState';

export default function Usuarios() {
  const [vinculados, setVinculados] = useState([]);
  const [perfis, setPerfis] = useState([]);
  const [loading, setLoading] = useState(true);

  const [busca, setBusca] = useState('');
  const [resultadosBusca, setResultadosBusca] = useState([]);
  const [buscando, setBuscando] = useState(false);
  const [perfilVincular, setPerfilVincular] = useState('');

  const [editandoId, setEditandoId] = useState(null);

  const carregar = () => {
    setLoading(true);
    Promise.all([api.get('/usuarios'), api.get('/perfis')])
      .then(([u, p]) => { setVinculados(u.data); setPerfis(p.data); })
      .catch(console.error)
      .finally(() => setLoading(false));
  };

  useEffect(() => { carregar(); }, []);

  const buscarUsuarios = async () => {
    if (busca.length < 2) return;
    setBuscando(true);
    try {
      const res = await api.get('/usuarios/buscar', { params: { nome: busca } });
      setResultadosBusca(res.data);
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'Erro', text: err.response?.data?.error || 'Erro ao buscar' });
    } finally { setBuscando(false); }
  };

  const vincular = async (usuarioId) => {
    if (!perfilVincular) { Swal.fire({ icon: 'warning', title: 'Selecione um perfil' }); return; }
    try {
      await api.post('/usuarios/vincular', { usuario_id: usuarioId, perfil_id: parseInt(perfilVincular) });
      Swal.fire({ icon: 'success', title: 'Vinculado!', timer: 1500, showConfirmButton: false });
      setBusca(''); setResultadosBusca([]); setPerfilVincular('');
      carregar();
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'Erro', text: err.response?.data?.error || 'Erro ao vincular' });
    }
  };

  const mudarPerfil = async (usuarioId, perfilId) => {
    try {
      await api.put(`/usuarios/${usuarioId}/perfil`, { perfil_id: parseInt(perfilId) });
      Swal.fire({ icon: 'success', title: 'Perfil atualizado!', timer: 1500, showConfirmButton: false });
      setEditandoId(null);
      carregar();
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'Erro', text: err.response?.data?.error || 'Erro ao atualizar' });
    }
  };

  const desvincular = async (usuario) => {
    const ok = await Swal.fire({
      icon: 'warning',
      title: `Desvincular ${usuario.nome}?`,
      text: 'O usuário perderá acesso ao portal.',
      showCancelButton: true,
      confirmButtonText: 'Desvincular',
      confirmButtonColor: '#dc3545',
      cancelButtonText: 'Cancelar',
    });
    if (!ok.isConfirmed) return;
    try {
      await api.delete(`/usuarios/${usuario.id}`);
      Swal.fire({ icon: 'success', title: 'Desvinculado!', timer: 1500, showConfirmButton: false });
      carregar();
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'Erro', text: err.response?.data?.error || 'Erro ao desvincular' });
    }
  };

  return (
    <div>
      <h4 className="fw-bold mb-4">
        <i className="bi bi-people me-2" style={{ color: 'var(--primary)' }}></i>
        Usuários do Portal
      </h4>

      {/* Vincular novo */}
      <div className="card p-4 mb-4">
        <h6 className="fw-bold mb-1" style={{ color: 'var(--primary)' }}>
          <i className="bi bi-link-45deg me-2"></i>Vincular usuário
        </h6>
        <p className="text-muted mb-3" style={{ fontSize: '0.82rem' }}>
          Busque um usuário da base do Centrão e vincule ao portal com o perfil desejado.
        </p>
        <div className="d-flex gap-2 flex-wrap align-items-end mb-3">
          <div>
            <label className="form-label" style={{ fontSize: '0.85rem' }}>Nome do usuário</label>
            <input
              className="form-control"
              value={busca}
              onChange={e => setBusca(e.target.value)}
              placeholder="ex: JOÃO"
              onKeyDown={e => e.key === 'Enter' && buscarUsuarios()}
            />
          </div>
          <div>
            <label className="form-label" style={{ fontSize: '0.85rem' }}>Perfil de acesso</label>
            <select className="form-select" value={perfilVincular} onChange={e => setPerfilVincular(e.target.value)}>
              <option value="">Selecione...</option>
              {perfis.map(p => <option key={p.id} value={p.id}>{p.nome}</option>)}
            </select>
          </div>
          <button
            className="btn-primary-arq"
            onClick={buscarUsuarios}
            disabled={buscando || busca.length < 2}
          >
            {buscando ? 'Buscando...' : <><i className="bi bi-search me-1"></i>Buscar</>}
          </button>
        </div>

        {resultadosBusca.length > 0 && (
          <div style={{ maxHeight: 220, overflowY: 'auto', border: '1px solid #e9ecef', borderRadius: 8 }}>
            {resultadosBusca.map(u => (
              <div key={u.id} className="d-flex align-items-center gap-3 py-2 px-3" style={{ borderBottom: '1px solid #f0f0f0' }}>
                <span className="fw-semibold" style={{ fontSize: '0.92rem' }}>{u.nome}</span>
                <button
                  className="btn-primary-arq ms-auto"
                  style={{ fontSize: '0.8rem', padding: '4px 12px' }}
                  onClick={() => vincular(u.id)}
                  disabled={!perfilVincular}
                >
                  <i className="bi bi-link-45deg me-1"></i>Vincular
                </button>
              </div>
            ))}
          </div>
        )}
        {resultadosBusca.length === 0 && busca.length >= 2 && !buscando && (
          <p className="text-muted mb-0" style={{ fontSize: '0.85rem' }}>Nenhum usuário encontrado ou todos já vinculados.</p>
        )}
      </div>

      {/* Lista de vinculados */}
      <div className="card">
        {loading ? (
          <div className="d-flex justify-content-center py-5">
            <div className="spinner-border" style={{ color: 'var(--primary)' }}></div>
          </div>
        ) : (
          <div className="tabela-container">
            <table className="table table-hover table-arq mb-0">
              <thead>
                <tr>
                  <th>Nome</th>
                  <th>Perfil</th>
                  <th>Status</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {vinculados.length === 0 ? (
                  <tr><td colSpan={4} style={{ padding: 0 }}>
                    <EmptyState
                      icone="bi-people"
                      titulo="Nenhum usuário vinculado ao portal"
                      texto="Use a busca acima pra encontrar usuários da base do Centrão e vincular ao portal."
                    />
                  </td></tr>
                ) : vinculados.map(u => (
                  <tr key={u.id}>
                    <td className="fw-semibold">{u.nome}</td>
                    <td>
                      {editandoId === u.id ? (
                        <select
                          className="form-select form-select-sm"
                          style={{ width: 'auto' }}
                          defaultValue={u.perfil_id}
                          onChange={e => mudarPerfil(u.id, e.target.value)}
                        >
                          {perfis.map(p => <option key={p.id} value={p.id}>{p.nome}</option>)}
                        </select>
                      ) : (
                        <span className="badge-filial">{u.perfil_nome}</span>
                      )}
                    </td>
                    <td>
                      <span className={u.ativo ? 'badge-positivo' : 'badge-neutro'}>
                        {u.ativo ? 'Ativo' : 'Inativo'}
                      </span>
                    </td>
                    <td>
                      <div className="d-flex gap-2">
                        <button
                          className="btn btn-sm btn-link p-0"
                          title="Editar perfil"
                          onClick={() => setEditandoId(editandoId === u.id ? null : u.id)}
                        >
                          <i className="bi bi-pencil" style={{ color: 'var(--primary)' }}></i>
                        </button>
                        <button
                          className="btn btn-sm btn-link p-0"
                          title="Desvincular"
                          onClick={() => desvincular(u)}
                        >
                          <i className="bi bi-x-circle" style={{ color: '#dc3545' }}></i>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      <p className="text-muted mt-2 mb-0" style={{ fontSize: '0.8rem' }}>{vinculados.length} usuário(s) vinculado(s)</p>
    </div>
  );
}
